import React from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { Municipality, getYearlyDataForYear } from '@/lib/data';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

interface FeeRadarChartProps {
  municipality: Municipality;
  allMunicipalities: Municipality[];
  selectedYear: number;
}

const ALL_FEE_KEYS = [
  { key: 'waterFee', label: 'Vann', id: 'water' },
  { key: 'sewageFee', label: 'Avløp', id: 'sewage' },
  { key: 'wasteFee', label: 'Renovasjon', id: 'waste' },
  { key: 'propertyTax', label: 'Septik', id: 'property' },
  { key: 'chimneySweepFee', label: 'Feiing', id: 'chimney' },
] as const;

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm text-slate-900 z-[9999]">
      <p className="font-semibold mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}:</span>
          <span className="font-medium">{Number(p.value).toLocaleString('nb-NO')} kr</span>
        </p>
      ))}
    </div>
  );
};

const FeeRadarChart: React.FC<FeeRadarChartProps> = ({
  municipality,
  allMunicipalities,
  selectedYear,
}) => {
  const { enabledCategories } = useFeeCategories();
  const FEE_KEYS = ALL_FEE_KEYS.filter(({ id }) => enabledCategories.includes(id));
  const muniData = getYearlyDataForYear(municipality, selectedYear);
  if (!muniData) return null;

  // Beregn landsgjennomsnitt per kategori
  const withYearData = allMunicipalities
    .filter((m) => m.historicalData.some((d) => d.year === selectedYear))
    .map((m) => m.historicalData.find((d) => d.year === selectedYear)!);

  const avgData = withYearData.length
    ? FEE_KEYS.reduce(
        (acc, { key }) => {
          acc[key] =
            withYearData.reduce((sum, d) => sum + (d[key] ?? 0), 0) / withYearData.length;
          return acc;
        },
        {} as Record<(typeof FEE_KEYS)[number]['key'], number>
      )
    : null;

  if (!avgData) return null;

  const globalMax = Math.max(
    ...FEE_KEYS.flatMap(({ key }) => [
      muniData[key] ?? 0,
      avgData[key] ?? 0,
    ]),
    1000
  );
  const fullMark = Math.ceil(globalMax * 1.2);

  const chartData = FEE_KEYS.map(({ key, label }) => ({
    subject: label,
    Kommune: Math.round(muniData[key] ?? 0),
    Landsgjennomsnitt: Math.round(avgData[key] ?? 0),
    fullMark,
  }));

  return (
    <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-slate-100">
      <h3 className="text-lg font-semibold mb-4">
        Sammenligning med landsgjennomsnitt ({selectedYear})
      </h3>
      <div className="h-80 md:h-96 lg:h-[28rem]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis
              dataKey="subject"
              tick={{ fontSize: 12 }}
              tickLine={false}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, fullMark]}
              tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
              tick={{ fontSize: 10 }}
            />
            <Radar
              name={municipality.name}
              dataKey="Kommune"
              stroke="#4375C0"
              fill="#4375C0"
              fillOpacity={0.4}
              strokeWidth={2}
            />
            <Radar
              name="Landsgjennomsnitt"
              dataKey="Landsgjennomsnitt"
              stroke="#5B9BD5"
              fill="#5B9BD5"
              fillOpacity={0.2}
              strokeWidth={2}
              strokeDasharray="4 4"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Standardbolig 120 m². Sammenligner {municipality.name} med snitt for{' '}
        {withYearData.length} kommuner.
      </p>
    </div>
  );
};

export default FeeRadarChart;
